import { useState } from 'react'

// No look-alike characters (0/O, 1/l/I), so a generated password is easy to read out or retype.
const ALPHABET = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%*?'
const GENERATED_LENGTH = 16

function randomPassword() {
  const out: string[] = []
  const bytes = new Uint8Array(1)
  // Rejection sampling keeps every character equally likely.
  const limit = 256 - (256 % ALPHABET.length)
  while (out.length < GENERATED_LENGTH) {
    crypto.getRandomValues(bytes)
    if (bytes[0] < limit) out.push(ALPHABET[bytes[0] % ALPHABET.length])
  }
  return out.join('')
}

const inputClass = 'w-full border border-rule bg-paper px-3 py-2.5 text-[15px] outline-none focus:border-ink'
const smallButton = 'text-[11px] tracking-[0.16em] uppercase text-muted hover:text-rose disabled:opacity-40'

export default function PasswordField({
  label,
  value,
  onChange,
  generate = false,
  onGenerated,
  autoComplete = 'new-password',
}: {
  label: string
  value: string
  onChange: (value: string) => void
  generate?: boolean
  /** Also called with a generated password, e.g. to fill a confirm field. */
  onGenerated?: (value: string) => void
  autoComplete?: string
}) {
  const [visible, setVisible] = useState(false)
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      setVisible(true)
    }
  }

  return (
    <div>
      <label className="block">
        <span className="text-caps mb-1.5 block text-[10px] text-muted">{label}</span>
        <input
          type={visible ? 'text' : 'password'}
          required
          autoComplete={autoComplete}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className={`${inputClass} font-mono`}
        />
      </label>
      <span className="mt-2 flex flex-wrap gap-x-5 gap-y-1">
        {generate ? (
          <button
            type="button"
            className={smallButton}
            onClick={() => {
              const password = randomPassword()
              onChange(password)
              onGenerated?.(password)
              setVisible(true)
            }}
          >
            Generate
          </button>
        ) : null}
        <button type="button" className={smallButton} onClick={() => setVisible((shown) => !shown)}>
          {visible ? 'Hide' : 'Show'}
        </button>
        {generate ? (
          <button type="button" className={smallButton} disabled={!value} onClick={() => void copy()}>
            {copied ? 'Copied' : 'Copy'}
          </button>
        ) : null}
      </span>
    </div>
  )
}
